import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { Provider } from "../order";
import { FulfillmentGroupData } from "./fulfillment-group";
import {
  canSplitStatus,
  FulfillmentGroupStatus,
} from "./fulfillment-group-status";
import { ShopifyFulfillmentHoldReason } from "./shopify-fulfillment-schemas";

const attributesForNewFulfillmentGroupsSchema_ = z.object({
  internalNote: z.string().nullish(),
  status: createEnum(FulfillmentGroupStatus),
  holdDetails: z
    .object({
      onHoldUntil: z.date().nullish(),
      reason: createEnum(ShopifyFulfillmentHoldReason),
    })
    .nullish(),
});
export const attributesForNewFulfillmentGroupsSchema: z.ZodType<AttributesForNewFulfillmentGroups> =
  attributesForNewFulfillmentGroupsSchema_;
export interface AttributesForNewFulfillmentGroups extends z.infer<
  typeof attributesForNewFulfillmentGroupsSchema_
> {}

const ItemToSplitSchema_ = z.object({
  fulfillmentOrderLineItemId: z.string(),
  quantity: z.number(),
});
export const ItemToSplitSchema: z.ZodType<ItemToSplit> = ItemToSplitSchema_;
export interface ItemToSplit extends z.infer<typeof ItemToSplitSchema_> {}

export function canSplitGroup(fulfillmentGroup: FulfillmentGroupData): {
  canSplit: boolean;
  disabledMessage: string | undefined;
} {
  const totalQuantity = fulfillmentGroup.items.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  const teamId = fulfillmentGroup.teamInfo?._id?.toString();
  if (!teamId) {
    return {
      canSplit: false,
      disabledMessage: "Cannot split shipment without a team",
    };
  }

  const canSplit =
    totalQuantity > 1 &&
    canSplitStatus(fulfillmentGroup.status) &&
    canSplitProviders(fulfillmentGroup.providers || [], teamId);

  return {
    canSplit,
    disabledMessage: getSplitDisabledMessage(
      fulfillmentGroup.status,
      fulfillmentGroup.providers,
      totalQuantity,
      teamId,
    ),
  };
}

// BC splitting is gated to an allowlist while the new multi-shipment
// marketplace split path is rolled out. Once the rollout is complete this
// list goes away and BC moves to the always-supported set.
const BIG_COMMERCE_SPLIT_ALLOWED_TEAM_IDS: ReadonlySet<string> = new Set([
  "6998ef3f17664a6118d66f86",
  // Production dev account
  "67a0f922d12fe0cffd48be1e",
  "6a0774405e4e59ae48fa772e",
  "69fcefe2eb934123d53b0e90",
  "6a0b7486082fe697ac4cd7e9",
]);

// TODO make splitting work for all providers
export function canSplitProviders(
  providers: Provider[],
  teamId: string,
): boolean {
  const unsupportedProviders = [
    Provider.AMAZON,
    Provider.EBAY,
    Provider.CIN7_OMNI,
    Provider.AIMS360,
    Provider.FAIRE,
    Provider.WOOCOMMERCE,
    Provider.TIKTOK,
    Provider.LINNWORKS,
  ];

  if (providers.some((p) => unsupportedProviders.includes(p))) {
    return false;
  }

  if (providers.includes(Provider.BIGCOMMERCE)) {
    return BIG_COMMERCE_SPLIT_ALLOWED_TEAM_IDS.has(teamId);
  }

  return true;
}

export function getSplitDisabledMessage(
  status: FulfillmentGroupStatus | null | undefined,
  providers: Provider[] | null | undefined,
  totalQuantity: number,
  teamId: string,
): string | undefined {
  if (totalQuantity <= 1) {
    return "Cannot split shipment with only 1 item";
  }
  if (!canSplitStatus(status)) {
    return `Cannot split shipment that is ${status}`;
  }
  if (!canSplitProviders(providers || [], teamId)) {
    return "Splitting shipments from this marketplace is not yet supported";
  }
  return undefined;
}
